import { buildArrivalNotification, selectPlantArrivals } from '../../domain/plants/plantArrivals';
import { NOTIFICATION_STATUS } from '../../domain/pwa/notificationOffer';
import { readNotificationsMuted } from '../pwa/offerPreferences';
import { fetchCatalogue, fetchProse } from '../../infrastructure/content/plantContentClient';
import { interpolate, translate } from '../../domain/i18n/messages';
import { getNotificationRegistration, getPlantNotificationCapability, showAppNotification } from './plantNotificationCapability';
import {
    mirrorPlantWatchState,
    readMirroredPlantWatchState,
    readSeenPlants,
    writeLastCheckedAt,
    writeSeenPlants,
} from './plantArrivalStore';

/**
 * Noticing that the catalogue grew, and saying so (SPEC-016).
 *
 * The counterpart to `appUpdate.js`, and built the same way for the same reason:
 * there is no server here to push an event (CON-003), so the app finds out by
 * READING the origin and comparing what it got with what it remembers.
 *
 * The seen list is recorded on every check, whether or not notifications are
 * permitted. That is deliberate. If recording waited for permission, every plant
 * added between a visitor's first open and the day they opt in would count as
 * new the moment they did — a welcome that arrives as a pile of five
 * notifications about plants that have been there for months.
 */

// A plant can only arrive with a deploy, and SPEC-010 already watches for those
// every minute. This is the slow backstop for a tab left open for days.
const ARRIVAL_CHECK_INTERVAL_MS = 30 * 60 * 1000;

/** Matched by the `periodicsync` handler in `assets/service-worker.js`. */
const ARRIVAL_SYNC_TAG = 'plant-arrivals';

let watchHandle = null;
let activeCheck = null;
let activeCheckKey = null;

function commonNameOf(prose, fallbackProse, plantId) {
    const record = (prose && prose[plantId]) || (fallbackProse && fallbackProse[plantId]) || null;

    return record && typeof record.commonName === 'string' && record.commonName.trim()
        ? record.commonName.trim()
        : plantId;
}

/**
 * What a gardener calls each arrival.
 *
 * Names live in the prose file, not the catalogue — the catalogue carries only
 * the botanical name, and "Cyperus longus has joined" is not what anyone calls
 * it. Its own try/catch, because the seen list has ALREADY been committed by the
 * time this runs: letting a failed prose fetch fall to the outer catch would
 * leave the arrival recorded as seen and never announced, with no later check
 * able to recover it. A notification naming the plant by its id is worse copy
 * and a far better outcome.
 */
async function readPlantNames(languageCode, arrivals) {
    try {
        const { prose, fallbackProse } = await fetchProse(languageCode);

        return Object.fromEntries(
            arrivals.map((plantId) => [plantId, commonNameOf(prose, fallbackProse, plantId)]),
        );
    } catch (error) {
        return {};
    }
}

/** The arrival, resolved into the visitor's language and handed to the shared seam. */
function showArrivalNotification(notification, languageCode) {
    return showAppNotification({
        title: interpolate(translate(languageCode, notification.titleKey), notification.values),
        body: interpolate(translate(languageCode, notification.bodyKey), notification.values),
        tag: notification.tag,
        url: notification.url,
        data: { plantIds: notification.plantIds },
    });
}

/**
 * One pass: read the catalogue, diff it, record it, and announce what is new.
 *
 * Returns what it did rather than nothing, so the hook and the tests can both
 * see the outcome without reaching into storage.
 */
export async function checkForPlantArrivals({ localeCode = '', languageCode = 'en' } = {}) {
    const checkKey = `${localeCode}:${languageCode}`;

    // Keyed, because a locale switch restarts the watch: an unkeyed guard would
    // hand the new locale the old locale's check, and the mirror the worker
    // reads would keep pointing at the language the visitor just left.
    if (activeCheck && activeCheckKey === checkKey) {
        return activeCheck;
    }

    activeCheckKey = checkKey;
    activeCheck = (async () => {
        // Read BEFORE the awaited fetch, not after. A permission granted while
        // this fetch is in flight would otherwise turn what began as a silent
        // recording pass into an announcement — which is exactly what opting in
        // is designed never to produce.
        // Muting is a decision the visitor made in the drawer, so it is read
        // here beside the permission and for the same reason: what a pass may
        // say is settled before it starts (SPEC-018).
        const isMuted = readNotificationsMuted();
        const mayAnnounce = !isMuted
            && getPlantNotificationCapability().status === NOTIFICATION_STATUS.granted;

        try {
            const [plants, mirrored] = await Promise.all([fetchCatalogue(), readMirroredPlantWatchState()]);
            const cataloguePlantIds = plants.map((plant) => plant && plant.id).filter(Boolean);
            const local = readSeenPlants();

            // The union of both records. The worker can only update the mirror,
            // so a plant it announced in the background is absent from Web
            // Storage — and would be announced a second time here.
            const { arrivals, nextSeenPlantIds, isSeeding } = selectPlantArrivals({
                cataloguePlantIds,
                seenPlantIds: [...new Set([...local.plantIds, ...(mirrored ? mirrored.seenPlantIds : [])])],
                hasSeenCatalogue: local.hasSeenCatalogue || Boolean(mirrored),
            });

            // Recorded before anything is shown. A notification that fires and
            // then fails to be recorded would fire again on the next check, and
            // the same plant announced twice is worse than one announced late.
            if (nextSeenPlantIds.length > 0) {
                const checkedAt = Date.now();

                writeSeenPlants(nextSeenPlantIds);
                writeLastCheckedAt(checkedAt);
                await mirrorPlantWatchState({
                    localeCode,
                    seenPlantIds: nextSeenPlantIds,
                    isMuted,
                    lastCheckedAt: checkedAt,
                });
            }

            if (isSeeding || arrivals.length === 0 || !mayAnnounce) {
                return { arrivals: isSeeding ? [] : arrivals, isSeeding, notified: false };
            }

            const notification = buildArrivalNotification({
                arrivals,
                plantNames: await readPlantNames(languageCode, arrivals),
                localeCode,
            });

            return {
                arrivals,
                isSeeding: false,
                notified: await showArrivalNotification(notification, languageCode),
            };
        } catch (error) {
            // Offline, a timed-out fetch, a rewritten 404 that parsed as HTML.
            // The next check is the recovery, exactly as in `appUpdate.js`.
            return { arrivals: [], isSeeding: false, notified: false, error };
        } finally {
            activeCheck = null;
            activeCheckKey = null;
        }
    })();

    return activeCheck;
}

/**
 * Ask the browser to keep checking while the app is closed (SPEC-016).
 *
 * Periodic Background Sync is Chromium-only, installed-apps-only, and the
 * browser decides both whether to grant it and how often to fire — a request,
 * not a schedule. Everywhere else this is a no-op that costs one `try`, and the
 * page's own check on open remains the whole mechanism. On iOS in particular
 * there is no background path at all, which is stated plainly in SPEC-016 rather
 * than papered over here.
 *
 * The tag is matched by the `periodicsync` handler in `assets/service-worker.js`.
 */
export async function ensureBackgroundArrivalSync({
    navigatorObject = typeof navigator === 'undefined' ? null : navigator,
    minIntervalMs = 12 * 60 * 60 * 1000,
} = {}) {
    if (!navigatorObject || !('serviceWorker' in navigatorObject)) {
        return false;
    }

    try {
        // Through the same bounded lookup the notification path uses. `ready`
        // never rejects and never settles without a registration, and this is
        // awaited from the opt-in press — an unbounded wait here is a button
        // that never finishes.
        const registration = await getNotificationRegistration({ navigatorObject });

        if (!registration || !registration.periodicSync) {
            return false;
        }

        // Re-registering an existing tag is harmless, but asking first keeps the
        // common case free of a rejected promise on every app start.
        const tags = await registration.periodicSync.getTags();

        if (Array.isArray(tags) && tags.includes(ARRIVAL_SYNC_TAG)) {
            return true;
        }

        await registration.periodicSync.register(ARRIVAL_SYNC_TAG, { minInterval: minIntervalMs });

        return true;
    } catch (error) {
        // Not installed, permission not granted, or the API is absent. None of
        // these is a failure worth reporting: there is no screen for it, and the
        // foreground check is unaffected.
        return false;
    }
}

/**
 * Check now, then on every return to the app, then on a slow timer.
 *
 * Returns its own teardown. Started from the hook rather than from bootstrap
 * because it needs the active locale, which only exists below the router.
 */
export function startPlantArrivalWatch({
    localeCode = '',
    languageCode = 'en',
    windowObject = typeof window === 'undefined' ? null : window,
    documentObject = typeof document === 'undefined' ? null : document,
    intervalMs = ARRIVAL_CHECK_INTERVAL_MS,
} = {}) {
    stopPlantArrivalWatch();

    const check = () => {
        checkForPlantArrivals({ localeCode, languageCode });
    };

    check();

    // Only once the visitor has already said yes. Registering earlier would ask
    // for a second permission on behalf of someone who has not agreed to the
    // first, and there would be nothing for it to announce anyway.
    if (getPlantNotificationCapability().status === NOTIFICATION_STATUS.granted) {
        ensureBackgroundArrivalSync();
    }

    if (!windowObject) {
        return () => {};
    }

    const timerId = windowObject.setInterval(check, intervalMs);

    // A backgrounded page's timers are throttled to nothing, so on a phone the
    // interval alone would never fire. Coming back to the app is the real check.
    const handleVisibilityChange = () => {
        if (!documentObject || documentObject.visibilityState === 'visible') {
            check();
        }
    };

    if (documentObject) {
        documentObject.addEventListener('visibilitychange', handleVisibilityChange);
    }

    watchHandle = () => {
        windowObject.clearInterval(timerId);

        if (documentObject) {
            documentObject.removeEventListener('visibilitychange', handleVisibilityChange);
        }
    };

    return watchHandle;
}

export function stopPlantArrivalWatch() {
    if (watchHandle) {
        watchHandle();
        watchHandle = null;
    }
}

export { ARRIVAL_CHECK_INTERVAL_MS, ARRIVAL_SYNC_TAG };
